import React from "react"
import PropTypes from "prop-types"
import {
  Box,
  TextField,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material"
import i18n from "i18n"
import {
  isValidCreditorId,
  isValidIban,
  isValidMandateRef,
  normalizeCreditorId,
  normalizeIban,
  normalizeMandateRef,
} from "components/Invoices/utils/invoiceUtils"

// Normalised on blur rather than on each keystroke, so these can be typed in readable
// groups. The backend normalises and rejects again, since the API is public.
export const SEPA_FIELDS = [
  {
    field: "accountNumber",
    labelKey: "contacts.accountNumber",
    normalize: normalizeIban,
    isValid: isValidIban,
  },
  {
    field: "mandateRef",
    labelKey: "contacts.mandateRef",
    normalize: normalizeMandateRef,
    isValid: isValidMandateRef,
  },
  {
    field: "creditorId",
    labelKey: "contacts.creditorId",
    normalize: normalizeCreditorId,
    isValid: isValidCreditorId,
  },
]

const ContabilityTab = ({ fields, handleChange, organizationId }) => {
  const handleSepaBlur = (field, normalize) => event => {
    const normalized = normalize(event.target.value)
    if (normalized === event.target.value) return
    handleChange({
      target: { name: `contability.${field}`, value: normalized },
    })
  }

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={2} sx={{ mt: 0, mb: 3 }}>
        <Grid item xs={4}>
          <TextField
            label={i18n.t("contacts.internalCode")}
            name="contability.code"
            value={fields.contability?.code || ""}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>{i18n.t("contacts.paymentMethod")}</InputLabel>
            <Select
              label={i18n.t("contacts.paymentMethod")}
              name="contability.paymentMethod"
              value={fields.contability?.paymentMethod || ""}
              onChange={handleChange}
            >
              <MenuItem value="transfer">
                {i18n.t("contacts.transfer")}
              </MenuItem>
              <MenuItem value="directDebit">
                {i18n.t("contacts.directDebit")}
              </MenuItem>
              <MenuItem value="cash">{i18n.t("contacts.cash")}</MenuItem>
              <MenuItem value="creditCard">{i18n.t("contacts.card")}</MenuItem>
              <MenuItem value="check">{i18n.t("contacts.check")}</MenuItem>
              <MenuItem value="other">{i18n.t("contacts.other")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            label={i18n.t("contacts.paymentPeriod")}
            name="contability.paymentPeriod"
            value={fields.contability?.paymentPeriod || ""}
            onChange={handleChange}
            fullWidth
            type="number"
          />
        </Grid>
      </Grid>
      <Typography variant="subtitle1">
        {i18n.t("contacts.directDebitData")}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0, mb: 3 }}>
        {SEPA_FIELDS.map(({ field, labelKey, normalize, isValid }) => {
          const value = fields.contability?.[field] || ""
          const invalid = !!value && !isValid(value)
          return (
            <Grid item xs={4} key={field}>
              <TextField
                label={i18n.t(labelKey)}
                name={`contability.${field}`}
                value={value}
                onChange={handleChange}
                onBlur={handleSepaBlur(field, normalize)}
                error={invalid}
                helperText={invalid ? i18n.t(`${labelKey}Invalid`) : ""}
                fullWidth
              />
            </Grid>
          )
        })}
      </Grid>
      <Typography variant="subtitle1">
        {i18n.t("contacts.responsiblePerson")}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={8}>
          <TextField
            label={i18n.t("contacts.fullName")}
            name="contability.responsibleName"
            value={fields.contability?.responsibleName || ""}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Email"
            name="contability.responsibleEmail"
            value={fields.contability?.responsibleEmail || ""}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
      </Grid>
    </Box>
  )
}

ContabilityTab.propTypes = {
  fields: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  organizationId: PropTypes.string,
}

export default ContabilityTab
